import dayjs from 'dayjs';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { toLocalISOString } from '../../../shared/lib/utils';
import type { RecordSubscription } from 'pocketbase';
import { Assignment, createAssignment, fetchAllAssignmentsByTaskId, resolveAssignment } from '../../../entities/assignment';
import { pb } from '../../../shared/api/pocketbase';
import { Lead } from '../../../entities/lead/model/types';
import { CardModalAssignmentsController } from './cardModalTypes';

export const isAssignmentExpired = (planDate: string, nowTs: number): boolean => {
  const iso = planDate.trim().replace(' ', 'T');
  const withTz = /Z$|[+-]\d{2}:\d{2}$/.test(iso) ? iso : iso + 'Z';
  return dayjs(withTz).valueOf() < nowTs;
};

interface Params {
  lead: Lead | null;
  isOpen: boolean;
  onNotify: (message: string) => void;
}

export const useCardModalAssignments = ({
  lead,
  isOpen,
  onNotify,
}: Params): CardModalAssignmentsController => {
  const [assignments, setAssignments] = useState<Assignment[]>([]);
  const [isAssignmentLoading, setIsAssignmentLoading] = useState(false);
  const [overdueOpenId, setOverdueOpenId] = useState<string | null>(null);
  const [overdueResolveTexts, setOverdueResolveTexts] = useState<Record<string, string>>({});
  const [overdueResolvingId, setOverdueResolvingId] = useState<string | null>(null);
  const [nowTs, setNowTs] = useState(() => Date.now());
  const leadId = lead?.id ?? null;
  const realtimeHandlerRef = useRef<((e: RecordSubscription<Assignment>) => void) | null>(null);
  const locallyResolvedIdsRef = useRef<Set<string>>(new Set());

  useEffect(() => {
    if (!isOpen || !leadId) {
      setAssignments([]);
      setIsAssignmentLoading(false);
      setOverdueOpenId(null);
      setOverdueResolveTexts({});
      return;
    }

    setIsAssignmentLoading(true);
    fetchAllAssignmentsByTaskId(leadId)
      .then(setAssignments)
      .catch(() => setAssignments([]))
      .finally(() => setIsAssignmentLoading(false));
  }, [isOpen, leadId]);

  useEffect(() => {
    if (!isOpen || !leadId) return;

    const FIELDS = 'id,name,planDate,status,result,created,updated,tasksId,authorId,ownerId,expand.authorId.id,expand.authorId.name,expand.authorId.email,expand.ownerId.id,expand.ownerId.name,expand.ownerId.email';

    const handler = async (e: RecordSubscription<Assignment>) => {
      if (e.action !== 'update') return;
      if (e.record.tasksId !== leadId) return;
      if (locallyResolvedIdsRef.current.has(e.record.id)) {
        locallyResolvedIdsRef.current.delete(e.record.id);
        return;
      }

      try {
        const hydrated = await pb.collection('jobsTasks').getOne<Assignment>(e.record.id, {
          expand: 'authorId,ownerId',
          fields: FIELDS,
        });
        setAssignments(prev => prev.map(a => a.id === hydrated.id ? hydrated : a));
      } catch {}
    };

    realtimeHandlerRef.current = handler;
    pb.collection('jobsTasks').subscribe<Assignment>('*', handler);

    return () => {
      if (realtimeHandlerRef.current) {
        pb.collection('jobsTasks').unsubscribe('*', realtimeHandlerRef.current);
        realtimeHandlerRef.current = null;
      }
    };
  }, [isOpen, leadId]);

  useEffect(() => {
    const hasPendingAssignments = assignments.some(a => !a.result && a.status !== 'EXPIRED');
    if (!isOpen || !hasPendingAssignments) {
      return;
    }

    let intervalId: number | undefined;
    const delayToNextTick = Math.max(0, 30_000 - (Date.now() % 30_000));

    const timeoutId = window.setTimeout(() => {
      setNowTs(Date.now());
      intervalId = window.setInterval(() => {
        setNowTs(Date.now());
      }, 30_000);
    }, delayToNextTick);

    return () => {
      window.clearTimeout(timeoutId);
      if (intervalId !== undefined) {
        window.clearInterval(intervalId);
      }
    };
  }, [assignments, isOpen]);

  const handleResolveAssignment = useCallback(async (id: string, text: string) => {
    try {
      locallyResolvedIdsRef.current.add(id);
      const resolved = await resolveAssignment(id, text);
      setAssignments(prev => prev.map(a => a.id === id ? resolved : a));
    } catch {
      locallyResolvedIdsRef.current.delete(id);
      onNotify('Ошибка при сохранении ответа');
    }
  }, [onNotify]);

  const handleResolveOverdue = useCallback(async (id: string) => {
    const text = (overdueResolveTexts[id] ?? '').trim();
    if (!text) return;

    setOverdueResolvingId(id);
    try {
      locallyResolvedIdsRef.current.add(id);
      const resolved = await resolveAssignment(id, text);
      setAssignments(prev => prev.map(a => a.id === id ? resolved : a));
      setOverdueResolveTexts(prev => {
        const next = { ...prev };
        delete next[id];
        return next;
      });
      setOverdueOpenId(null);
    } catch {
      locallyResolvedIdsRef.current.delete(id);
      onNotify('Ошибка при сохранении ответа');
    } finally {
      setOverdueResolvingId(null);
    }
  }, [onNotify, overdueResolveTexts]);

  const handleAssignmentCreate = useCallback(async (data: { name: string; ownerId: string; planDate: string }) => {
    if (!lead) {
      return;
    }

    try {
      const created = await createAssignment({
        name: data.name,
        tasksId: lead.id,
        ownerId: data.ownerId,
        planDate: toLocalISOString(data.planDate),
      });
      setAssignments(prev => [...prev, created].sort((a, b) => a.planDate.localeCompare(b.planDate)));
    } catch {
      onNotify('Ошибка при создании задания');
    }
  }, [lead, onNotify]);

  const completedAssignments = useMemo(
    () => assignments.filter(a => !!a.result || a.status === 'EXPIRED' || isAssignmentExpired(a.planDate, nowTs)),
    [assignments, nowTs],
  );

  const pendingAssignments = useMemo(
    () => assignments.filter(a => !a.result && a.status !== 'EXPIRED' && !isAssignmentExpired(a.planDate, nowTs)),
    [assignments, nowTs],
  );

  return {
    assignments,
    pendingAssignments,
    completedAssignments,
    isAssignmentLoading,
    overdueOpenId,
    overdueResolveTexts,
    overdueResolvingId,
    nowTs,
    setOverdueResolveTexts,
    setOverdueOpenId,
    handleResolveAssignment,
    handleResolveOverdue,
    handleAssignmentCreate,
  };
};
